import * as React from "react";

import { useState } from "react";
import { useAccessPermission } from "@/helpers/acessPermission";

// Third Party
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";

// UI
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { TableLoading } from "@/components/Skeleton";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { ArrowLeft, PencilLine } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { getTaxDetails } from "@/services/tax-obligations";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import NumberFormatSelector from "@/components/NumberFormatSelector";
import FileHandleViewer from "@/components/FileHandleViewer";

// Utils and Services
// import { S3_BUCKET_LINK } from "@/components/constants";

interface TaxDetails {
  profitTax: number;
  addIncomeTax: number;
  monthlyTax: number;
  valueAddedTax: number;
  exciseTax: number;
  dividendTax: number;
  otherTaxes: number;
  totalTaxes: number;
}

interface TaxDetailsResponse {
  status: boolean;
  message: string;
  data: {
    currentTaxPlan: TaxDetails;
    currentTaxExecution: TaxDetails;
    currentTaxDiffs: TaxDetails;
    currentTaxPctDiffs: TaxDetails;
    lastTaxExecution: TaxDetails | null;
    lastTaxDiffs: TaxDetails | null;
    lastTaxPctDiffs: TaxDetails | null;
    yearly: number;
    docFile: string;
    companyName: string;
    companyNameEng: string | null;
    companyProfileUrl: string;
  };
}

export const Route = createFileRoute(
  "/_layout-private/report/tax-obligations/tax-liability-plan/"
)({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const companyId = String(localStorage.getItem("companyId"));
  const yearly = Number(localStorage.getItem("yearly"));
  const { delegationPlan } = useAccessPermission();

  const [unit, setUnit] = useState("1");

  // Query
  const { data, isLoading } = useQuery({
    queryKey: ["tax-obligations-Details", companyId, yearly],
    queryFn: () =>
      getTaxDetails({
        companyId,
        obligationType: "PLAN",
        yearly,
      }),
    enabled: !!companyId,
  });

  const tax = data?.data as TaxDetailsResponse["data"];

  return (
    <section>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink>
              <Link
                replace
                to="/report/tax-obligations?active=taxLiabilityPlan"
              >
                ລາຍງານແຜນມອບພັນທະອາກອນ
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>ລາຍງານພັນທະອາກອນ</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <Card className="mt-8">
        <CardHeader>
          <button
            type="button"
            onClick={() =>
              navigate({
                to: "/report/tax-obligations?active=taxLiabilityPlan",
              })
            }
            className="flex items-center gap-1 mb-4"
            aria-label="Go back to tax obligations report"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>
          <CardTitle>ລາຍລະອຽດແຜນມອບພັນທະໃຫ້ລັດງວດປີ {tax?.yearly}</CardTitle>
          <CardDescription>ລາຍລະອຽດ</CardDescription>
          <div className="flex items-end justify-end w-full my-5 gap-x-2">
            {delegationPlan.isUpdate && (
              <Button
                onClick={() => {
                  navigate({
                    to: "/report/tax-obligations/tax-liability-plan/edit",
                  });
                }}
                variant="secondary"
                className="flex items-center border border-blue-500 bg-none gap-x-1 w-[120px] rounded-md p-2 text-blue-500"
              >
                <PencilLine /> ແກ້ໄຂ
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <TableLoading />
          ) : (
            <div className="space-y-4">
              <NumberFormatSelector unit={unit} setUnit={setUnit} />
              <Table className="border">
                {/* <TableCaption>A list of your recent invoices.</TableCaption> */}
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-semibold text-black bg-gray-100 border-x-2">
                      ເນື້ອໃນ
                    </TableHead>
                    <TableHead className="font-semibold text-black bg-gray-100 border-x-2">
                      ມອບຕົວຈິງ {yearly}
                    </TableHead>
                    <TableHead className="font-semibold text-black bg-gray-100 border-x-2">
                      ແຜນມອບເງິນ {yearly}
                    </TableHead>
                    <TableHead className="font-semibold text-black bg-gray-100 border-x-2">
                      ຈຳນວນປ່ຽນແປງ
                    </TableHead>
                    <TableHead className="font-semibold text-black bg-gray-100 border-x-2">
                      ທຽບໃສ່ແຜນປີ {yearly}
                    </TableHead>
                    <TableHead className="font-semibold text-black bg-gray-100 border-x-2">
                      ມອບຕົວຈິງ (ປີທີ່ຜ່ານມາ)
                    </TableHead>
                    <TableHead className="font-semibold text-black bg-gray-100 border-x-2">
                      ຈຳນວນປ່ຽນແປງ
                    </TableHead>
                    <TableHead className="font-semibold text-black bg-gray-100 border-x-2">
                      ທຽບໃສ່ປີທີ່ຜ່ານມາ
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Profit Tax */}
                  <TableRow>
                    <TableCell className="border-x">ອາກອນກຳໄລ</TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(
                          tax?.currentTaxExecution?.profitTax?.toFixed(2)
                        ) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(
                              tax?.currentTaxExecution?.profitTax?.toFixed(2)
                            ) / Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxPlan?.profitTax) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxPlan?.profitTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxDiffs?.profitTax) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxDiffs?.profitTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {tax?.currentTaxPctDiffs?.profitTax
                        ? `${tax.currentTaxPctDiffs.profitTax}%`
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.lastTaxExecution?.profitTax) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.lastTaxExecution?.profitTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.lastTaxDiffs?.profitTax) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.lastTaxDiffs?.profitTax) / Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {tax?.lastTaxPctDiffs?.profitTax
                        ? `${tax.lastTaxPctDiffs.profitTax}%`
                        : "-"}
                    </TableCell>
                  </TableRow>

                  {/* Additional Income Tax */}
                  <TableRow>
                    <TableCell>ອາກອນກຳໄລມອບເກີນ ຫຼື ມອບເພີ່ມ</TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxExecution?.addIncomeTax) /
                          Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxExecution?.addIncomeTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxPlan?.addIncomeTax) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxPlan?.addIncomeTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxDiffs?.addIncomeTax) /
                          Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxDiffs?.addIncomeTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {tax?.currentTaxPctDiffs?.addIncomeTax
                        ? `${tax.currentTaxPctDiffs.addIncomeTax}%`
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.lastTaxExecution?.addIncomeTax) /
                          Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.lastTaxExecution?.addIncomeTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.lastTaxDiffs?.addIncomeTax) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.lastTaxDiffs?.addIncomeTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {tax?.lastTaxPctDiffs?.addIncomeTax
                        ? `${tax.lastTaxPctDiffs.addIncomeTax}%`
                        : "-"}
                    </TableCell>
                  </TableRow>

                  {/* Monthly Tax */}
                  <TableRow>
                    <TableCell>ອາກອນເງິນເດືອນ</TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxExecution?.monthlyTax) /
                          Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxExecution?.monthlyTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxPlan?.monthlyTax) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxPlan?.monthlyTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxDiffs?.monthlyTax) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxDiffs?.monthlyTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {tax?.currentTaxPctDiffs?.monthlyTax
                        ? `${tax.currentTaxPctDiffs.monthlyTax}%`
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.lastTaxExecution?.monthlyTax) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.lastTaxExecution?.monthlyTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.lastTaxDiffs?.monthlyTax) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.lastTaxDiffs?.monthlyTax) / Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {tax?.lastTaxPctDiffs?.monthlyTax
                        ? `${tax.lastTaxPctDiffs.monthlyTax}%`
                        : "-"}
                    </TableCell>
                  </TableRow>

                  {/* Value Added Tax */}
                  <TableRow>
                    <TableCell>ອາກອນມູນຄ່າເພີ່ມ (ອມພ)</TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxExecution?.valueAddedTax) /
                          Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxExecution?.valueAddedTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxPlan?.valueAddedTax) /
                          Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxPlan?.valueAddedTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxDiffs?.valueAddedTax) /
                          Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxDiffs?.valueAddedTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {tax?.currentTaxPctDiffs?.valueAddedTax
                        ? `${tax.currentTaxPctDiffs.valueAddedTax}%`
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.lastTaxExecution?.valueAddedTax) /
                          Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.lastTaxExecution?.valueAddedTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.lastTaxDiffs?.valueAddedTax) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.lastTaxDiffs?.valueAddedTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {tax?.lastTaxPctDiffs?.valueAddedTax
                        ? `${tax.lastTaxPctDiffs.valueAddedTax}%`
                        : "-"}
                    </TableCell>
                  </TableRow>

                  {/* Excise Tax */}
                  <TableRow>
                    <TableCell>ອາກອນຊົມໃຊ້</TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxExecution?.exciseTax) /
                          Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxExecution?.exciseTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxPlan?.exciseTax) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxPlan?.exciseTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxDiffs?.exciseTax) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxDiffs?.exciseTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {tax?.currentTaxPctDiffs?.exciseTax
                        ? `${tax.currentTaxPctDiffs.exciseTax}%`
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.lastTaxExecution?.exciseTax) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.lastTaxExecution?.exciseTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.lastTaxDiffs?.exciseTax) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.lastTaxDiffs?.exciseTax) / Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {tax?.lastTaxPctDiffs?.exciseTax
                        ? `${tax.lastTaxPctDiffs.exciseTax}%`
                        : "-"}
                    </TableCell>
                  </TableRow>

                  {/* Dividend Tax */}
                  <TableRow>
                    <TableCell>ອາກອນເງິນປັນຜົນ</TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxExecution?.dividendTax) /
                          Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxExecution?.dividendTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxPlan?.dividendTax) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxPlan?.dividendTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxDiffs?.dividendTax) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxDiffs?.dividendTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {tax?.currentTaxPctDiffs?.dividendTax
                        ? `${tax.currentTaxPctDiffs.dividendTax}%`
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.lastTaxExecution?.dividendTax) /
                          Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.lastTaxExecution?.dividendTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.lastTaxDiffs?.dividendTax) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.lastTaxDiffs?.dividendTax) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {tax?.lastTaxPctDiffs?.dividendTax
                        ? `${tax.lastTaxPctDiffs.dividendTax}%`
                        : "-"}
                    </TableCell>
                  </TableRow>

                  {/* Other Taxes */}
                  <TableRow>
                    <TableCell>ອາກອນອື່ນໆ</TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxExecution?.otherTaxes) /
                          Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxExecution?.otherTaxes) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxPlan?.otherTaxes) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxPlan?.otherTaxes) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxDiffs?.otherTaxes) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxDiffs?.otherTaxes) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {tax?.currentTaxPctDiffs?.otherTaxes
                        ? `${tax.currentTaxPctDiffs.otherTaxes}%`
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.lastTaxExecution?.otherTaxes) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.lastTaxExecution?.otherTaxes) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.lastTaxDiffs?.otherTaxes) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.lastTaxDiffs?.otherTaxes) / Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {tax?.lastTaxPctDiffs?.otherTaxes
                        ? `${tax.lastTaxPctDiffs.otherTaxes}%`
                        : "-"}
                    </TableCell>
                  </TableRow>

                  {/* Total Taxes */}
                  <TableRow>
                    <TableCell>ລວມມອບອາກອນໃນປີ</TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxExecution?.totalTaxes) /
                          Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxExecution?.totalTaxes) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxPlan?.totalTaxes) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxPlan?.totalTaxes) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.currentTaxDiffs?.totalTaxes) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.currentTaxDiffs?.totalTaxes) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {tax?.currentTaxPctDiffs?.totalTaxes
                        ? `${tax.currentTaxPctDiffs.totalTaxes}%`
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.lastTaxExecution?.totalTaxes) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.lastTaxExecution?.totalTaxes) /
                            Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {Number.isNaN(
                        Number(tax?.lastTaxDiffs?.totalTaxes) / Number(unit)
                      )
                        ? "-"
                        : (
                            Number(tax?.lastTaxDiffs?.totalTaxes) / Number(unit)
                          ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right border-x">
                      {tax?.lastTaxPctDiffs?.totalTaxes
                        ? `${tax.lastTaxPctDiffs.totalTaxes}%`
                        : "-"}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}

          {tax?.docFile
            ? delegationPlan.isDownload && (
                <div className="mt-2">
                  <FileHandleViewer fileUrl={tax?.docFile} />{" "}
                </div>
              )
            : null}
        </CardContent>
      </Card>
    </section>
  );
}
